import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { papi } from "@/lib/papi";
import { apiError } from "@/lib/api";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { toast } from "sonner";
import { Loading, SectionCard } from "@/components/common/Primitives";
import { KpiCard } from "@/components/common/KpiCard";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ShieldHalf, Building2, Users, CheckCircle2, Ban, Plus, Loader2, LogOut,
  Power, KeyRound, Trash2, Copy, Pencil,
} from "lucide-react";

const PLAN_MODULES = { starter: ["telekom"], professional: ["telekom", "energie"], business: ["telekom", "energie", "versicherung"] };
const PLANS = [
  { key: "starter", label: "Starter · 79 €" },
  { key: "professional", label: "Professional · 149 €" },
  { key: "business", label: "Business · 299 €" },
];
const MODULES = [
  { key: "telekom", label: "Telekommunikation" },
  { key: "energie", label: "Energie" },
];

export default function Platform() {
  const { status, admin, logout } = usePlatformAuth();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [deleteTenant, setDeleteTenant] = useState(null);
  const [credDialog, setCredDialog] = useState(null);

  const { data: stats } = useQuery({ queryKey: ["pstats"], queryFn: async () => (await papi.get("/platform/stats")).data, enabled: status === "authed" });
  const { data, isLoading } = useQuery({ queryKey: ["ptenants"], queryFn: async () => (await papi.get("/platform/tenants")).data, enabled: status === "authed" });

  if (status === "checking") return <div className="flex min-h-screen items-center justify-center"><Loading /></div>;
  if (status === "guest") return <Navigate to="/platform/login" replace />;

  const refetch = () => { qc.invalidateQueries({ queryKey: ["ptenants"] }); qc.invalidateQueries({ queryKey: ["pstats"] }); };

  const toggleStatus = async (t) => {
    try {
      if (t.status === "active") await papi.post(`/platform/tenants/${t.id}/suspend`, { confirm: true });
      else await papi.post(`/platform/tenants/${t.id}/reactivate`);
      toast.success(t.status === "active" ? "Mandant gesperrt" : "Mandant reaktiviert");
      refetch();
    } catch (err) { toast.error(apiError(err)); }
  };

  const resetOwner = async (t) => {
    try {
      const { data: r } = await papi.post(`/platform/tenants/${t.id}/reset-owner`);
      setCredDialog({ title: "Inhaber-Zugang zurückgesetzt", email: r.owner_email, password: r.temp_password });
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-border bg-[hsl(var(--sidebar))] px-4 text-white sm:px-6">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent text-accent-foreground"><ShieldHalf className="h-4 w-4" /></span>
          <div className="leading-tight">
            <div className="text-sm font-semibold">Plattform-Administration</div>
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--sidebar-muted))]">System-Inhaber</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-[hsl(var(--sidebar-muted))] sm:inline">{admin?.name}</span>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={logout} data-testid="platform-logout">
            <LogOut className="mr-1.5 h-4 w-4" /> Abmelden
          </Button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1200px] px-4 py-6 sm:px-6">
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiCard testId="pstat-total" label="Mandanten" value={stats?.tenants_total ?? "—"} icon={Building2} />
          <KpiCard testId="pstat-active" label="Aktiv" value={stats?.tenants_active ?? "—"} icon={CheckCircle2} />
          <KpiCard testId="pstat-suspended" label="Gesperrt" value={stats?.tenants_suspended ?? "—"} icon={Ban} tone="danger" />
          <KpiCard testId="pstat-users" label="Benutzer gesamt" value={stats?.users_total ?? "—"} icon={Users} tone="info" />
        </div>

        <SectionCard title="Mandanten" action={
          <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setCreateOpen(true)} data-testid="create-tenant-button">
            <Plus className="mr-1.5 h-4 w-4" /> Mandant anlegen
          </Button>
        }>
          {isLoading ? <Loading /> : (
            <div className="space-y-2">
              {(data?.items || []).map((t) => (
                <div key={t.id} data-testid={`tenant-row-${t.id}`}
                  className="flex flex-col gap-3 rounded-lg border border-border p-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold">{t.name}</span>
                      <Badge variant="secondary" className="font-mono text-[10px]">{t.slug}</Badge>
                      {t.status === "active"
                        ? <Badge className="bg-emerald-500/10 text-emerald-700 text-[10px]">Aktiv</Badge>
                        : <Badge className="bg-red-500/10 text-red-700 text-[10px]">Gesperrt</Badge>}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      <span className="capitalize">Tarif: {t.plan}</span>
                      <span>Module: {t.modules.join(", ") || "—"}</span>
                      <span>Inhaber: {t.owner_email || "—"}</span>
                      <span>Kunden {t.usage.customers} · Verträge {t.usage.contracts} · Benutzer {t.usage.users}/{t.max_users ?? "—"}</span>
                      <span>seit {fmtDate(t.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditTenant(t)} data-testid={`edit-tenant-${t.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Tarif/Module</Button>
                    <Button variant="outline" size="sm" onClick={() => resetOwner(t)} data-testid={`reset-owner-${t.id}`}><KeyRound className="mr-1 h-3.5 w-3.5" />Zugang</Button>
                    <Button variant="outline" size="sm" onClick={() => toggleStatus(t)} data-testid={`toggle-tenant-${t.id}`}>
                      <Power className="mr-1 h-3.5 w-3.5" />{t.status === "active" ? "Sperren" : "Aktivieren"}
                    </Button>
                    <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleteTenant(t)} data-testid={`delete-tenant-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-4 text-[11px] text-muted-foreground">Der System-Inhaber verwaltet ausschließlich Mandanten, Tarife und Module — er hat keinen Einblick in Kundendaten, Verträge oder Dokumente der Mandanten. Alle Aktionen werden protokolliert.</p>
        </SectionCard>
      </main>

      <TenantCreateDialog open={createOpen} onOpenChange={setCreateOpen} onDone={(cred) => { refetch(); if (cred) setCredDialog(cred); }} />
      <TenantEditDialog tenant={editTenant} onOpenChange={() => setEditTenant(null)} onDone={refetch} />

      {/* delete confirm */}
      <AlertDialog open={!!deleteTenant} onOpenChange={() => setDeleteTenant(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mandant endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              „{deleteTenant?.name}" und <strong>alle</strong> zugehörigen Daten (Kunden, Verträge, Dokumente, Benutzer) werden unwiderruflich gelöscht. Diese Aktion wird protokolliert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-tenant"
              onClick={async () => {
                try { await papi.delete(`/platform/tenants/${deleteTenant.id}?confirm=true`); toast.success("Mandant gelöscht"); setDeleteTenant(null); refetch(); }
                catch (err) { toast.error(apiError(err)); }
              }}>Endgültig löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* one-time credentials */}
      <Dialog open={!!credDialog} onOpenChange={() => setCredDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle>{credDialog?.title || "Zugangsdaten"}</DialogTitle>
            <DialogDescription>Dieses temporäre Passwort wird nur einmal angezeigt. Geben Sie es sicher weiter — es wird nicht im Klartext gespeichert.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm"><span className="text-muted-foreground">E-Mail:</span> <span className="font-medium">{credDialog?.email}</span></div>
            <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
              <span><span className="text-muted-foreground">Passwort:</span> <span className="font-mono font-medium">{credDialog?.password}</span></span>
              <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard?.writeText(credDialog.password); toast.success("Kopiert"); }}><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <DialogFooter><Button onClick={() => setCredDialog(null)}>Verstanden</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ModulePicker({ plan, value, onChange }) {
  const allowed = PLAN_MODULES[plan] || [];
  return (
    <div className="space-y-2">
      {MODULES.map((m) => {
        const can = allowed.includes(m.key);
        return (
          <label key={m.key} className={`flex items-center gap-2 text-sm ${!can ? "opacity-40" : ""}`}>
            <Checkbox checked={value.includes(m.key)} disabled={!can}
              onCheckedChange={(c) => onChange(c ? [...value, m.key] : value.filter((x) => x !== m.key))}
              data-testid={`module-check-${m.key}`} />
            {m.label}{!can && <span className="text-[10px]">(Tarif-Upgrade nötig)</span>}
          </label>
        );
      })}
    </div>
  );
}

function TenantCreateDialog({ open, onOpenChange, onDone }) {
  const [f, setF] = useState({ name: "", slug: "", plan: "starter", modules: ["telekom"], owner_email: "", owner_name: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await papi.post("/platform/tenants", f);
      toast.success("Mandant angelegt");
      onOpenChange(false);
      setF({ name: "", slug: "", plan: "starter", modules: ["telekom"], owner_email: "", owner_name: "" });
      onDone({ title: "Mandant & Inhaber-Zugang erstellt", email: data.owner_email, password: data.temp_password });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader><DialogTitle>Neuen Mandanten anlegen</DialogTitle>
          <DialogDescription>Legt ein Kundenunternehmen samt erstem Inhaber-Zugang an.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Firmenname</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} required data-testid="tenant-name-input" /></div>
          <div className="space-y-1.5"><Label>Slug (für Login-URL)</Label><Input value={f.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} placeholder="z. B. muster-gmbh" required data-testid="tenant-slug-input" /></div>
          <div className="space-y-1.5"><Label>Tarif</Label>
            <Select value={f.plan} onValueChange={(v) => set("plan", v) || set("modules", f.modules.filter((m) => (PLAN_MODULES[v] || []).includes(m)))}>
              <SelectTrigger data-testid="tenant-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PLANS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Branchenmodule</Label><ModulePicker plan={f.plan} value={f.modules} onChange={(v) => set("modules", v)} /></div>
          <div className="border-t border-border pt-4">
            <p className="mb-3 text-xs font-medium text-muted-foreground">Erster Inhaber-Benutzer</p>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} required data-testid="owner-name-input" /></div>
              <div className="space-y-1.5"><Label>E-Mail</Label><Input type="email" value={f.owner_email} onChange={(e) => set("owner_email", e.target.value)} required data-testid="owner-email-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" disabled={busy} data-testid="tenant-create-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Anlegen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TenantEditDialog({ tenant, onOpenChange, onDone }) {
  const [plan, setPlan] = useState("starter");
  const [modules, setModules] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (tenant) { setPlan(tenant.plan); setModules(tenant.modules || []); }
  }, [tenant]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await papi.patch(`/platform/tenants/${tenant.id}`, { plan, modules });
      toast.success("Mandant aktualisiert");
      onOpenChange(); onDone();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!tenant} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Tarif & Module — {tenant?.name}</DialogTitle>
          <DialogDescription>Passt Abonnement und freigeschaltete Branchenmodule dieses Mandanten an.</DialogDescription>
        </DialogHeader>
        {tenant && (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5"><Label>Tarif</Label>
              <Select value={plan} onValueChange={(v) => { setPlan(v); setModules((m) => m.filter((x) => (PLAN_MODULES[v] || []).includes(x))); }}>
                <SelectTrigger data-testid="edit-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PLANS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Branchenmodule</Label><ModulePicker plan={plan} value={modules} onChange={setModules} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onOpenChange}>Abbrechen</Button>
              <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" disabled={busy} data-testid="edit-tenant-submit">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Speichern
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
